/**
 * Payments API service. Mirrors backend src/services/payments.ts.
 */
import { apiClient } from './api';

export type PaymentMethod = 'cash' | 'check' | 'ach' | 'credit_card' | 'wire' | 'other';
export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded';

export interface Payment {
  id: number;
  invoice_id: number;
  invoice_number: string | null;
  customer_id: number | null;
  customer_name: string | null;
  amount: number;
  payment_method: PaymentMethod;
  payment_date: string;
  reference_number: string | null;
  notes: string | null;
  status: PaymentStatus;
  refund_of_payment_id: number | null;
  recorded_by_user_id: number;
  recorded_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentsSummary {
  total: number;
  completed: number;
  pending: number;
  refunded: number;
  collected_today: number;
  collected_this_week: number;
  outstanding_invoices_count: number;
  outstanding_invoices_total: number;
}

export interface CreatePaymentInput {
  invoice_id: number;
  amount: number;
  payment_method: PaymentMethod;
  payment_date?: string;
  reference_number?: string;
  notes?: string;
}

export interface ListPaymentsFilters {
  page?: number;
  limit?: number;
  invoiceId?: number;
  status?: PaymentStatus;
  method?: PaymentMethod;
  search?: string;
  fromDate?: string;
  toDate?: string;
}

export async function listPayments(filters: ListPaymentsFilters = {}): Promise<{
  data: Payment[];
  pagination: { total: number; page: number; limit: number; pages: number };
}> {
  return apiClient.get('/payments', filters as Record<string, any>);
}

export async function getPayment(id: number | string): Promise<Payment> {
  return apiClient.get<Payment>(`/payments/${id}`);
}

export async function createPayment(input: CreatePaymentInput): Promise<Payment> {
  return apiClient.post<Payment>('/payments', input);
}

export async function refundPayment(id: number | string, reason?: string): Promise<Payment> {
  return apiClient.post<Payment>(`/payments/${id}/refund`, { reason });
}

export async function getPaymentsSummary(): Promise<PaymentsSummary> {
  return apiClient.get<PaymentsSummary>('/payments/summary');
}
